import { Fragment, useState } from 'react'
import { Modal } from 'react-bootstrap'
import {
  bookColor,
  formatBook,
  formatOdds,
  formatSize,
  formatSportLimit,
  isSectionSuspended,
  sortSections,
} from './fancyFormat'
import './style.css'

/**
 * Shared renderer for the reference's fancy-type `.market-6` blocks
 * (Normal, fancy1, oddeven, meter). Markup copies the reference template:
 *   .market-6 > .bet-table > (.bet-table-header, .bet-table-body)
 *   per section: .fancy-tripple > .bet-table-row(s) [+ .sec-remark]
 *
 * layout:
 *   'noyes'   – header No(lay) / Yes(back); row = LAY1 box, BACK1 box (+ rows for level 2/3)
 *   'backlay' – header Back / Lay; row = BACK1 box (.back), LAY1 box (.lay)
 *   'oddeven' – header Odd / Even (both .back); row = BACK1 box, LAY1 box, both .back
 */
const HEADERS = {
  noyes: [['lay', 'No'], ['back', 'Yes']],
  backlay: [['back', 'Back'], ['lay', 'Lay']],
  oddeven: [['back', 'Odd'], ['back', 'Even']],
}

const findOdd = (section, oname) =>
  (section.odds || []).find((o) => String(o.oname || '').toLowerCase() === oname)

function OddsBox({ odd, boxClass }) {
  const odds = odd ? odd.odds : 0
  const size = odd ? odd.size : 0
  return (
    <div className={`bl-box ${boxClass}${Number(odds) === 0 ? ' no-val' : ''}`}>
      <span className="d-block odds">{formatOdds(odds)}</span>
      {Number(size) !== 0 && <span className="d-block">{formatSize(size)}</span>}
    </div>
  )
}

function FancyMarket({
  marketName,
  betLockName,
  layout = 'noyes',
  index = 1,
  onBetLockClick,
  marketsData = [],
  exposureData = {},
}) {
  const [showRunAmountModal, setShowRunAmountModal] = useState(false)
  const [runAmountData, setRunAmountData] = useState([])

  const market = marketsData.find((m) => (m.mname || m.marketName || m.name) === marketName)
  const sections = market && Array.isArray(market.section) ? sortSections(market.section) : []
  if (!sections.length) return null

  const mname = market.mname || marketName
  const marketOff = market.gscode != null && Number(market.gscode) !== 1

  // Sample data structure for run amounts (unchanged behaviour)
  const handleTitleClick = () => {
    setRunAmountData([
      { run: 10, amount: 100 },
      { run: 20, amount: 200 },
      { run: 30, amount: 300 },
      { run: 40, amount: 400 },
      { run: 50, amount: 500 },
    ])
    setShowRunAmountModal(true)
  }

  const renderLevelRow = (section, level, withName) => {
    const back = findOdd(section, `back${level}`)
    const lay = findOdd(section, `lay${level}`)
    if (!back) return null
    const suspended = !marketOff && isSectionSuspended(section)
    const title = section.nat || section.name || ''
    const book = exposureData[title]
    let boxes
    if (layout === 'backlay') {
      boxes = [<OddsBox key="b" odd={back} boxClass="back" />, lay && <OddsBox key="l" odd={lay} boxClass="lay" />]
    } else if (layout === 'oddeven') {
      boxes = [<OddsBox key="b" odd={back} boxClass="back" />, lay && <OddsBox key="l" odd={lay} boxClass="back" />]
    } else {
      boxes = [lay && <OddsBox key="l" odd={lay} boxClass="lay" />, <OddsBox key="b" odd={back} boxClass="back" />]
    }
    return (
      <div
        key={`lvl${level}`}
        data-title={section.gstatus || ''}
        className={`bet-table-row${suspended ? ' suspendedtext' : ''}`}
      >
        {withName ? (
          <div className="nation-name d-none-mobile">
            <p onClick={() => handleTitleClick(title)}>{title}</p>
            <p className="mb-0" style={{ color: bookColor(book) }}>{formatBook(book)}</p>
          </div>
        ) : (
          <div className="nation-name d-none-mobile" />
        )}
        {boxes}
        {withName && (
          <div className="fancy-min-max">
            {' Min:'}<span>{formatSportLimit(section.min)}</span>{' Max:'}<span>{formatSportLimit(section.max)}</span>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="market-6">
      <div className="bet-table">
        <div className="bet-table-header">
          <div data-toggle="collapse" data-target={`#market${index}`} aria-expanded="true" className="nation-name">
            <span title={mname}>{mname}</span>
          </div>{' '}
          <div className="float-right">
            <a
              href="#"
              className="btn btn-back"
              onClick={(e) => {
                e.preventDefault()
                if (onBetLockClick) onBetLockClick(betLockName)
              }}
            >
              Bet Lock
            </a>
          </div>
        </div>

        <div
          id={`market${index}`}
          data-title={market.status || ''}
          className={`bet-table-body collapse show${marketOff ? ' suspendedfull' : ''}`}
        >
          <div className="bet-table-row">
            <div className="text-right nation-name" />
            {HEADERS[layout].map(([cls, label]) => (
              <div key={label} className={`${cls} bl-title d-none-mobile`}>{label}</div>
            ))}
          </div>

          {sections.map((section, i) => (
            <Fragment key={section.sid ?? i}>
              <div className="fancy-tripple">
                {renderLevelRow(section, 1, true)}
                {layout === 'noyes' && renderLevelRow(section, 2, false)}
                {layout === 'noyes' && renderLevelRow(section, 3, false)}
              </div>
              {section.rem && String(section.rem).trim() !== '' && <div className="sec-remark">{section.rem}</div>}
            </Fragment>
          ))}
        </div>
      </div>

      {/* Run Amount Modal */}
      <Modal
        show={showRunAmountModal}
        onHide={() => setShowRunAmountModal(false)}
        dialogClassName="modal-md modal-dialog-scrollable"
      >
        <Modal.Header>
          <Modal.Title>Run Amount</Modal.Title>
          <button
            type="button"
            className="close text-white"
            onClick={() => setShowRunAmountModal(false)}
            aria-label="Close"
          >
            ×
          </button>
        </Modal.Header>
        <Modal.Body>
          <div className="table-responsive run-amount-container">
            <table className="table table-bordered">
              <thead>
                <tr>
                  <th>Run</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {runAmountData.length > 0 ? (
                  runAmountData.map((item, idx) => (
                    <tr key={idx}>
                      <td>{item.run}</td>
                      <td>{item.amount}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="2" className="text-center">No record found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  )
}

export default FancyMarket
